import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";

import { Observable, throwError } from "rxjs";
import { catchError, map } from "rxjs/operators";

import { AppSessionService } from "../app-session/app-session.service";

import { setAdvancedFilterQuery, setSimpleFilterQuery } from "../../utils/tableUtils";
import { FilterOperation } from "src/app/models/custom-table/custom-table.model";
import { GridUtils } from "../../utils/gridUtils";
import { TaxDTO } from "src/app/models/taxes/tax.model";

const EMPTY_STRING = "";

@Injectable({
  providedIn: "root",
})
export class TaxesService {
  constructor(
    private http: HttpClient,
    private appSession: AppSessionService
  ) {}
    
  private TAXES_URL = this.appSession.baseUrl + "/api/v1/taxes";
  private TAX_LEGAL_TYPES_URL = this.appSession.baseUrl + "/api/v1/taxes/taxLegalTypes/search";
  private TAX_LEGAL_SUB_TYPES_URL = this.appSession.baseUrl + "/api/v1/taxes/taxLegalSubTypes";
  private RATE_TYPES_URL = this.appSession.baseUrl + "/api/v1/taxes/rateTypes/search";
  private LEGAL_REGIMES_URL = this.appSession.baseUrl + "/api/v1/taxes/taxLegalRegimes/search";
  private LEGAL_REASONS_URL = this.appSession.baseUrl + "/api/v1/taxes/taxLegalReasons/search";

  getTax(code: string): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    const url = `${this.TAXES_URL}/${code}`;
    return this.http.get<any>(url, { headers }).pipe(
      map(({ result }) => result),
      catchError((error) => throwError(error))
    );
  }

  getTaxList(
    paging?: any,
    filteringArgs?: any,
    sortingArgs?: any,
    advancedFilteringArgs?: any
  ): Observable<any> {
    let headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    let filterQuery = EMPTY_STRING;
    let advancedFilterQuery = EMPTY_STRING;
    let orderQuery = EMPTY_STRING;

    if (filteringArgs) {
      filterQuery = setSimpleFilterQuery(filteringArgs);
    }

    if (advancedFilteringArgs) {
      advancedFilterQuery = setAdvancedFilterQuery(advancedFilteringArgs);
    }

    if (sortingArgs) {
      const { active, direction } = sortingArgs;
      orderQuery += active + "," + direction.toUpperCase();
    }

    if (filterQuery.length > 0 || advancedFilterQuery.length > 0) {
      headers = headers.append(
        "query",
        `${filterQuery} ${
          filterQuery.length > 0 && advancedFilterQuery.length > 0
            ? FilterOperation.AND
            : ""
        } ${advancedFilterQuery}`
      );
    }

    if (orderQuery.length > 0) {
      headers = headers.append("sort", orderQuery);
    }

    const url = paging
      ? this.buildDataUrl(paging, "/search?")
      : `${this.TAXES_URL}/search?`;

    return this.http.get<any>(url, { headers }).pipe(
      map(({ result }) => result),
      catchError((error) => throwError(error))
    );
  }

  createTax(tax: TaxDTO): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });
    const data = JSON.stringify(tax);
    const url = `${this.TAXES_URL}`;
    return this.http.post<any>(url, data, { headers }).pipe(
      map(({ result }) => result),
      catchError((error) => throwError(error))
    );
  }

  updateTax(tax: TaxDTO, code: string): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    const data = JSON.stringify(tax);
    const url = `${this.TAXES_URL}/${code}`;
    return this.http.put<any>(url, data, { headers }).pipe(
      map(({ result }) => result),
      catchError((error) => throwError(error))
    );
  }

  getTaxLegalTypes(): Observable<any> {
    let headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    const url = `${this.TAX_LEGAL_TYPES_URL}`;

    return this.http.get<any>(url, { headers }).pipe(
      map(({ result }) => result),
      catchError((error) => throwError(error))
    );
  }

  getTaxLegalSubTypes(advancedFilteringArgs?: any): Observable<any> {
    let headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    let advancedFilterQuery = EMPTY_STRING;

    if (advancedFilteringArgs) {
      advancedFilterQuery = setAdvancedFilterQuery(advancedFilteringArgs);
    }

    const url = `${this.TAX_LEGAL_SUB_TYPES_URL}/search`;

    if (advancedFilterQuery.length > 0) {
      headers = headers.append("query", advancedFilterQuery);
    }

    return this.http.get<any>(url, { headers }).pipe(
      map(({ result }) => result),
      catchError((error) => throwError(error))
    );
  }

  getRateTypes(): Observable<any> {
    let headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    const url = `${this.RATE_TYPES_URL}`;

    return this.http.get<any>(url, { headers }).pipe(
      map(({ result }) => result),
      catchError((error) => throwError(error))
    );
  }

  getLegalRegimes(): Observable<any> {
    let headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    const url = `${this.LEGAL_REGIMES_URL}`;

    return this.http.get<any>(url, { headers }).pipe(
      map(({ result }) => result),
      catchError((error) => throwError(error))
    );
  }

  getLegalReasons(): Observable<any> {
    let headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    const url = `${this.LEGAL_REASONS_URL}`;

    return this.http.get<any>(url, { headers }).pipe(
      map(({ result }) => result),
      catchError((error) => throwError(error))
    );
  }

  // Grid filter helpers block
  private buildDataUrl(pagingArgs: any, path: string): string {
    let baseQueryString = `${this.TAXES_URL}${path}`;
    let scrollingQuery = EMPTY_STRING;
    let query = EMPTY_STRING;

    if (pagingArgs) {
      scrollingQuery = GridUtils._buildScrollExpression(pagingArgs);
    }

    query += scrollingQuery !== EMPTY_STRING ? `&${scrollingQuery}` : EMPTY_STRING;
    baseQueryString += query;

    return baseQueryString;
  }
}
